/**
 * Static SEO copy + local-knowledge data per city.
 *
 * Why JS, not a CMS:
 *  - Five pages × five sections each are static enough to ship in the bundle.
 *  - Translating + tweaking copy stays in-repo so PRs review SEO changes.
 *  - Per-city OMI numbers come from the backend (single source of truth).
 *
 * Each entry:
 *   slug:         URL segment used by /citta/:slug
 *   name:         display name (used in <h1>)
 *   province:     2-letter Italian province code
 *   metaTitle:    <title> — keep under 60 chars for SERP truncation
 *   metaDesc:     <meta description> — keep under 160 chars
 *   intro:        2-3 paragraph hero copy for the city
 *   markets:      bullets describing the local sub-markets / quartieri
 *   constraints:  bullets listing the legal/planning constraints buyers MUST know
 *   investAngle:  one paragraph framing the investment thesis (yield, tourism, …)
 *   faq:          3 city-specific Q&A used to seed a per-page FAQPage schema
 */

export const CITIES = {
  lecce: {
    slug: "lecce",
    name: "Lecce",
    province: "LE",
    metaTitle: "Comprare casa a Lecce — Valutazione OMI 2026 | Deal Rating",
    metaDesc:
      "Quotazioni OMI, vincoli del centro storico e analisi annunci immobiliari di Lecce. Calcola in 20 secondi se un annuncio è un affare o sopravvalutato.",
    intro: [
      "Lecce è il fulcro economico del Salento e il mercato immobiliare più liquido della provincia. La domanda è sostenuta da due flussi distinti: la popolazione universitaria (che alimenta l'affitto residenziale lungo) e il turismo culturale (motore degli affitti brevi nel centro storico barocco).",
      "Le quotazioni OMI medie si attestano intorno ai 1.750 €/m², ma con una varianza enorme: dai 600-800 €/m² delle periferie post-belliche fino ai 3.500-4.000 €/m² dei palazzetti barocchi su via Vittorio Emanuele II o piazza Sant'Oronzo. La distorsione media-mediana rende essenziale un'analisi annuncio-per-annuncio.",
    ],
    markets: [
      "Centro storico (Sedile, Duomo, Sant'Oronzo): unico in Italia, ma quasi tutto vincolato. Resa da affitto turistico alta, ristrutturazioni complesse.",
      "Mura urbiche (via Trinchese, viale Marconi): edifici 1950-70 in buono stato, ottimi per affitto a studenti.",
      "Borgo Piave / Castromediano: zona residenziale di livello medio-alto, prezzi più stabili.",
      "Salesiani / via San Lazzaro: stock anni '80-'90, target famiglie locali, prezzi più bassi.",
      "Periferia est (Stadio, Ferrovia): zone in transizione, prezzi da valutare con cautela.",
    ],
    constraints: [
      "Vincolo Soprintendenza nel centro storico: ogni intervento esterno (infissi, intonaci, balconi) richiede nulla osta.",
      "ZTL estesa al centro storico: incide su comodità di parcheggio e sul prezzo percepito di B&B / appartamenti turistici.",
      "Conformità urbanistica e catastale: nei palazzetti antichi è frequente trovare difformità (soppalchi non autorizzati, divisioni interne).",
      "Classe energetica: spesso G nelle case storiche; calcolare costi di efficientamento prima dell'acquisto.",
    ],
    investAngle:
      "Lecce è una scommessa sul turismo culturale tutto-l'anno. L'affitto breve in centro storico rende il 6-9% lordo annuo se gestito bene, ma le ristrutturazioni sono onerose (1.200-1.800 €/m²). Per affitto residenziale lungo le yield reali viaggiano sul 4-5% netto.",
    faq: [
      {
        q: "Quanto costa al m² una casa in centro storico a Lecce?",
        a: "La media OMI è ~1.750 €/m² ma in centro storico si va da 2.000 €/m² per immobili da ristrutturare fino a 4.000 €/m² per palazzetti barocchi restaurati.",
      },
      {
        q: "Conviene comprare casa a Lecce per affitto turistico?",
        a: "Sì, se nel centro storico e ben ristrutturata. La domanda turistica è destagionalizzata grazie a eventi (Notte della Taranta, mercatini, congressi). Yield lordo realistico 6-9%.",
      },
      {
        q: "Quali vincoli ci sono nel centro di Lecce?",
        a: "Vincolo della Soprintendenza per qualsiasi intervento esterno e ZTL estesa. Prima di acquistare verificare conformità urbanistica e fattibilità delle modifiche interne previste.",
      },
    ],
  },

  ostuni: {
    slug: "ostuni",
    name: "Ostuni",
    province: "BR",
    metaTitle: "Comprare casa o trullo a Ostuni — Prezzi e OMI | Deal Rating",
    metaDesc:
      "Analisi del mercato immobiliare di Ostuni: prezzi medi, vincoli paesaggistici, trulli e case rurali nella Valle d'Itria. Valuta annunci gratis.",
    intro: [
      "Ostuni — la \"Città Bianca\" — è uno dei mercati più caldi e speculativi del Sud Italia. Sospinta dal turismo internazionale di fascia alta e dall'esposizione mediatica (Vogue, NYT), ha visto i prezzi raddoppiare in 10 anni mentre il borgo restava di fatto invariato.",
      "Le quotazioni OMI medie si attestano intorno ai 1.800 €/m² per il residenziale, ma le contrade collinari con vista mare e gli ulivi monumentali entrano in mercato premium da 2.500-4.000 €/m². Un trullo restaurato in contrada vale tipicamente 3.500-6.000 €/m² costruito.",
    ],
    markets: [
      "Centro storico (la Città Bianca): vincolato dalla Soprintendenza, prezzi premium, ottimo per affitto breve di lusso.",
      "Contrade collinari (Cisternino-Martina): trulli e masserie, terreni con ulivi, mercato internazionale.",
      "Fascia costiera (Costa Merlata, Pilone, Rosa Marina): vincoli pesanti, ma rendita turistica eccezionale.",
      "Periferia residenziale: meno appealing ma più accessibile per chi cerca prima casa.",
    ],
    constraints: [
      "Vincolo paesaggistico esteso a quasi tutto il territorio comunale (Piano Paesaggistico Territoriale Regionale).",
      "Fascia costiera dei 300 metri (vincolo Galasso): blocca nuove costruzioni e limita pesantemente le ristrutturazioni.",
      "Xylella fastidiosa: prima di acquistare un fondo con ulivi richiedere certificazione fitosanitaria recente.",
      "Trulli storici: cono originale vincolato come bene di interesse storico-architettonico. Vietati ampliamenti che modifichino la sagoma.",
      "Conformità catastale: molti trulli e lamie risultano accatastati in modo difforme dalla situazione reale. Sanatoria possibile ma costosa.",
    ],
    investAngle:
      "Ostuni è un mercato \"trofeo\". Si compra per la storia e la vista, non per il cashflow puro. Il trullo restaurato genera 25-40k€/anno di affitto breve in alta stagione, contro costi di gestione 15-25%. Plusvalenza storica del 6-8% annuo, ma il mercato sta perdendo slancio: attenzione al prezzo d'ingresso.",
    faq: [
      {
        q: "Quanto costa un trullo a Ostuni nel 2026?",
        a: "Un trullo già restaurato in contrada Ostuni costa tipicamente 150.000-400.000 € per 60-100 m² coperti, più terreno con ulivi. Da ristrutturare: 50.000-150.000 € più 1.500-2.500 €/m² di costi opera.",
      },
      {
        q: "Si può costruire vicino al mare a Ostuni?",
        a: "No nella fascia dei 300 m dalla battigia (vincolo Galasso). Sono possibili interventi solo di manutenzione su edifici esistenti, mai nuove edificazioni o ampliamenti volumetrici.",
      },
      {
        q: "Qual è il rendimento di un affitto turistico a Ostuni?",
        a: "Un trullo ben gestito su Airbnb/Booking genera 25.000-40.000 € lordi annui (alta stagione 90-95% occupazione). Yield netto realistico 4-7% considerando manutenzione, gestione e periodo basso (3-4 mesi).",
      },
    ],
  },

  otranto: {
    slug: "otranto",
    name: "Otranto",
    province: "LE",
    metaTitle: "Casa a Otranto — Mercato immobiliare e vincoli costa | Deal Rating",
    metaDesc:
      "Quotazioni OMI Otranto, vincoli fascia costiera e analisi annunci. Scopri se la casa al mare è un affare o sopravvalutata in 20 secondi.",
    intro: [
      "Otranto è il punto più orientale d'Italia e l'epicentro del turismo costiero del basso Salento. Il mercato è polarizzato: il centro storico medievale e la fascia litoranea (dalla baia dei Turchi a Porto Badisco) sono in mercato premium, mentre l'entroterra prima dell'agro otrantino offre ancora occasioni.",
      "Le quotazioni OMI medie si attestano intorno ai 2.600 €/m², ma le ville fronte mare superano facilmente i 4.000-5.000 €/m². Il vincolo dei 300 metri dalla costa rende ogni metro quadro vista mare estremamente prezioso (perché non replicabile).",
    ],
    markets: [
      "Centro storico (porta Alfonsina, castello): vincolato, prezzi alti, perfetto per affitto breve.",
      "Fascia litoranea nord (baia dei Turchi, Alimini): zone protette, costruito raro, premium puro.",
      "Litoranea sud (Porto Badisco, Santa Cesarea): immobili di pregio, costa rocciosa.",
      "Periferia est e contrada Frassinito: prezzi più accessibili, distanza dal centro 2-3 km.",
    ],
    constraints: [
      "Fascia costiera 300 m: vincolo Galasso esteso. Niente nuove costruzioni, ristrutturazioni solo conservative.",
      "Aree SIC / ZPS (Alimini, costa nord): vincoli ambientali aggiuntivi su attività e movimenti terra.",
      "Centro storico: vincolo Soprintendenza, accessi limitati, parcheggio scarso.",
      "Pozzi e cisterne: spesso non potabili, verificare allacci all'acquedotto pubblico.",
    ],
    investAngle:
      "Otranto è un mercato fortemente stagionale ma estremamente brandizzato. Affitti brevi a 200-450 €/notte in alta stagione (giugno-settembre), occupazione 80-95% in agosto. Yield realistico 5-7% lordo, ma la plusvalenza tiene grazie alla scarsità della vista mare a vincolo.",
    faq: [
      {
        q: "Quanto vale una casa fronte mare a Otranto?",
        a: "Una villa fronte mare a Otranto parte da 4.000 €/m² e supera spesso i 6.000 €/m² nelle zone più esclusive (baia dei Turchi, Conca Specchiulla). La rarità è dovuta al vincolo costiero che impedisce nuove costruzioni.",
      },
      {
        q: "Conviene comprare a Otranto entro la fascia dei 300 m?",
        a: "Sì come trofeo / rendita turistica, no per chi vuole ristrutturare pesante o ampliare. Il vincolo Galasso blocca ogni intervento che modifichi la sagoma o aumenti volumi.",
      },
      {
        q: "Quanto rende l'affitto turistico a Otranto?",
        a: "Una casa di 80 m² ben posizionata genera 18.000-30.000 € lordi/anno con stagionalità forte (60% del fatturato in luglio-agosto). Yield netto 4-6% al netto di gestione e periodi morti.",
      },
    ],
  },

  gallipoli: {
    slug: "gallipoli",
    name: "Gallipoli",
    province: "LE",
    metaTitle: "Casa a Gallipoli — Investimento immobiliare e prezzi | Deal Rating",
    metaDesc:
      "Mercato immobiliare di Gallipoli: prezzi OMI, zone turistiche, affitti brevi e vincoli costieri. Valuta annunci immobiliari gratis.",
    intro: [
      "Gallipoli è il polo del turismo giovane e movida del Salento, con un mercato immobiliare fortemente orientato all'affitto breve. La domanda esplode nei mesi giugno-settembre, mentre nei mesi invernali la città è sostanzialmente in letargo turistico.",
      "Le quotazioni OMI medie sono intorno ai 2.300 €/m², ma il centro storico sul promontorio (la \"Città Vecchia\") e gli appartamenti fronte mare a Baia Verde superano i 3.000-3.500 €/m². L'investitore tipo qui è uno che cerca cashflow estivo, non una casa per uso proprio.",
    ],
    markets: [
      "Città Vecchia (centro storico sul mare): borgo bianco fortificato, prezzi premium per affitto breve.",
      "Lungomare Marconi / Galateo: appartamenti vista porto, ottimi per turisti.",
      "Baia Verde / Lido San Giovanni: zone balneari con stabilimenti, premium estivo.",
      "Rivabella / Padula Bianca: zone in transizione, prezzi accessibili.",
    ],
    constraints: [
      "Fascia costiera 300 m: vincolo Galasso, nessuna nuova costruzione vista mare.",
      "Centro storico: Soprintendenza, ZTL, parcheggio quasi impossibile in alta stagione.",
      "Stagionalità: alcune aree pratiche solo da giugno a settembre, calcolare bene il vuoto invernale.",
      "Regolamento affitti brevi del Comune: verificare obblighi locali e tassa di soggiorno.",
    ],
    investAngle:
      "Gallipoli è il caso di scuola del turismo balneare puro. Affitti estivi a 130-280 €/notte luglio-agosto, occupazione 95%, ma 8 mesi di stagione morta. Yield lordo 7-10% se la posizione è top, scende al 4-5% in zone meno richieste. Plusvalenza incerta, dipende dal trend del turismo giovane.",
    faq: [
      {
        q: "Quanto costa un appartamento a Gallipoli vista mare?",
        a: "Un appartamento di 50-60 m² fronte mare a Baia Verde costa 180.000-280.000 €. In Città Vecchia si va da 2.500 €/m² (da ristrutturare) a 3.800 €/m² (restaurato).",
      },
      {
        q: "Quanto rende l'affitto breve a Gallipoli?",
        a: "In alta stagione (luglio-agosto) 130-280 €/notte con occupazione 90-95%. Fatturato annuo tipico 10.000-22.000 € per appartamento da 50 m². Yield netto realistico 6-9%.",
      },
      {
        q: "Conviene investire a Gallipoli o Otranto?",
        a: "Gallipoli ha cashflow estivo più alto ma stagionalità più dura. Otranto ha rendite più equilibrate e domanda internazionale più solida. Per pura plusvalenza: Otranto. Per yield estivo: Gallipoli.",
      },
    ],
  },

  taranto: {
    slug: "taranto",
    name: "Taranto",
    province: "TA",
    metaTitle: "Casa a Taranto — Mercato immobiliare e quotazioni OMI | Deal Rating",
    metaDesc:
      "Prezzi case a Taranto: OMI città vecchia, borgo, marine. Calcola in 20 secondi se un annuncio è un affare reale o speculazione.",
    intro: [
      "Taranto è il mercato immobiliare più sottovalutato del meridione adriatico. Penalizzato dalla questione ILVA e da una scarsa narrativa turistica, offre però quotazioni OMI nettamente inferiori al Salento leccese e brindisino, con potenziale upside legato alla riqualificazione urbana (Mar Piccolo, città vecchia, ex Arsenale).",
      "Le quotazioni OMI medie cittadine si attestano intorno ai 900-1.100 €/m², con la Città Vecchia su due mari che presenta immobili da pochi euro al m² (perché disastrati strutturalmente) fino a 2.000-2.500 €/m² per quelli ristrutturati con vista mare. Le marine (Lido Azzurro, Marina di Pulsano) hanno mercati separati con dinamiche tipicamente turistiche.",
    ],
    markets: [
      "Città Vecchia (isola tra i due mari): patrimonio storico unico, immobili sottocosto ma con costi di restauro elevatissimi.",
      "Borgo Nuovo (centro città): mercato residenziale lungo, prezzi 1.000-1.500 €/m².",
      "Tamburi, Paolo VI: zone vicine all'ILVA, prezzi più bassi ma con questioni ambientali e di valore percepito.",
      "Marine taluni (Castellaneta Marina, Marina di Pulsano, Lido Azzurro): stagionali, prezzi 1.200-2.500 €/m².",
    ],
    constraints: [
      "Vincoli ambientali AERCA: l'Area Rete Ecologica Comunale impone restrizioni in certe zone industriali e periurbane.",
      "Città Vecchia: Soprintendenza + Piano di Recupero in attesa, alcuni isolati con problemi strutturali gravi.",
      "Aree contaminate (vicinanza ILVA): verificare bonifiche ed eventuali certificazioni dei terreni.",
      "Affitti brevi: regolamentazione comunale meno permissiva del Salento leccese.",
    ],
    investAngle:
      "Taranto è una scommessa di lungo periodo sulla riqualificazione urbana. Chi compra oggi un palazzo in Città Vecchia a 30.000-60.000 € può ritrovarsi tra 10 anni con un asset da 200.000+ € se la transizione post-ILVA decolla. Cashflow attuale modesto (affitti residenziali 350-550 €/mese), ma il rapporto rischio/rendimento è asimmetrico.",
    faq: [
      {
        q: "Quanto costa una casa nella Città Vecchia di Taranto?",
        a: "Da poche decine di migliaia di euro per immobili disastrati a 2.500 €/m² per quelli restaurati con affaccio sul mare. Calcolare sempre 1.500-2.500 €/m² di costi di restauro su immobili storici.",
      },
      {
        q: "Conviene comprare casa a Taranto nel 2026?",
        a: "Sì se si crede nella riqualificazione post-ILVA (10-15 anni di orizzonte). No se si cerca cashflow turistico immediato — Taranto non è ancora un brand turistico forte come il resto del Salento.",
      },
      {
        q: "Quali zone di Taranto evitare?",
        a: "Tamburi e Paolo VI hanno questioni ambientali pesanti (vicinanza ILVA). Non sono \"da evitare\" in assoluto, ma il valore percepito ne risente. Verificare sempre la zona col PUG comunale.",
      },
    ],
  },
};

export const CITY_SLUGS = Object.keys(CITIES);
